const projects = [
  {
    slug: 'orchestrator',
    repository: 'https://github.com/antoine-marchal/OrchestratorServer',
    number: '01',
    name: 'Orchestrator',
    eyebrow: 'Flagship · Automation and integration',
    tagline: 'A practical control plane for moving data, connecting systems, cleaning datasets, and operating repeatable automation.',
    description:
      'My flagship project: a self-hosted automation platform created for the integration work I repeatedly needed to perform across applications, data, runtimes, and machines.',
    image: '/projects/orchestrator/demo/flowdemo1edit.png',
    alt: 'Orchestrator core data pipeline with connected executable steps and live output',
    imagePosition: 'center top',
    tags: ['React', 'TypeScript', 'DAG engine', 'Distributed workers'],
    highlights: ['Data migration and cleaning flows', 'Cross-system and cross-runtime integration', 'Governed, traceable execution'],
    featured: true,
    facts: [
      ['4', 'Trigger models'],
      ['7+', 'Execution modes'],
      ['3', 'Operational roles'],
      ['1', 'Control plane'],
    ],
    problem:
      'Data migrations, application interfaces, and data-cleaning jobs often begin as useful scripts, then spread across schedulers, machines, file shares, and individual knowledge. I created Orchestrator to bring that work into one visible system: a place to design the flow, keep its code and artifacts together, execute each step in the right environment, and understand what happened afterwards.',
    audience:
      'Platform teams, industrial IT, operations engineering, integration teams, and organizations coordinating heterogeneous automation across servers and workstations.',
    tour: [
      {
        title: 'Turn an operating process into an executable graph',
        copy: 'Model data, code, variables, and artifacts as one readable pipeline while keeping the execution context and live output in view.',
        image: '/projects/orchestrator/demo/flowdemo1edit.png',
        alt: 'Orchestrator core data pipeline with variable, processing steps, files, artifacts, and console output',
      },
      {
        title: 'Make routing rules visible',
        copy: 'Express conditional paths directly in the workflow, so operators can understand which decision sends work to approval, review, or rejection.',
        image: '/projects/orchestrator/demo/flowdemo2.png',
        alt: 'Orchestrator conditional routing workflow with approval, manual review, and rejection branches',
      },
      {
        title: 'Supervise every decision live',
        copy: 'See the chosen route, skipped branches, node timings, inputs, outputs, and logs in one execution record.',
        image: '/projects/orchestrator/demo/executiondemo2.png',
        alt: 'Successful conditional workflow execution with selected branch, timeline, inputs, outputs, and logs',
      },
      {
        title: 'Design failure as part of the flow',
        copy: 'Configure retries and non-critical failure paths explicitly instead of hiding recovery behavior inside scripts.',
        image: '/projects/orchestrator/demo/demo3flow.png',
        alt: 'Orchestrator retry and recovery workflow with recoverable and non-critical failure steps',
      },
      {
        title: 'Prove recovery in the execution record',
        copy: 'Confirm retry attempts, tolerated failures, final status, and downstream completion without reconstructing events from scattered logs.',
        image: '/projects/orchestrator/demo/executiondemo3.png',
        alt: 'Orchestrator execution record showing successful retry, tolerated failure, timeline, and final output',
      },
    ],
    more: [
      {
        title: 'Worker estate',
        copy: 'Register and supervise remote execution workers, including their heartbeat, operating system, group, and access policy.',
        image: '/projects/orchestrator/workers.png',
        alt: 'Orchestrator worker administration with an online Windows worker',
      },
      {
        title: 'Secret management',
        copy: 'Keep credentials out of workflow code and resolve centrally managed secret references only when an authorized run needs them.',
        image: '/projects/orchestrator/secrets.png',
        alt: 'Orchestrator secret administration workspace',
      },
      {
        title: 'Users and roles',
        copy: 'Separate administration, operation, and read-only access in the same control plane used to design and run flows.',
        image: '/projects/orchestrator/rbac.png',
        alt: 'Orchestrator role-based access administration',
      },
      {
        title: 'Flow catalogue',
        copy: 'Keep the automation portfolio searchable and visible instead of distributing it across folders, servers, and personal task schedulers.',
        image: '/projects/orchestrator/flows.png',
        alt: 'Orchestrator flow catalogue',
      },
    ],
    capabilities: [
      ['Visual workflow design', 'Compose DAG-based flows with JavaScript, Python, Groovy, PowerShell, Batch, Shell, browser automation, conditions, and reusable subflows.'],
      ['Distributed execution', 'Delegate OS-specific or compute-heavy nodes to self-registering remote workers; workers initiate outbound long-poll connections, so they need no inbound port.'],
      ['Flexible activation', 'Run on demand, on cron schedules, from named events, or through expiring authenticated HTTP trigger tokens.'],
      ['Git-native lifecycle', 'Every flow is a real internal Git repository with commits, branches, checkout, restore, smart-HTTP clone, pull, and push.'],
      ['Execution variants', 'Run and schedule the same workflow against different branch and worker combinations without duplicating its definition.'],
      ['Live supervision', 'Stream worker logs into the browser with Server-Sent Events and retain node-level state, timing, result, and execution history.'],
      ['Artifact lifecycle', 'Attach binaries and reference files using content-addressed storage, 8 MB chunking for large payloads, streamed transfer, deduplication, and garbage collection.'],
      ['Platform administration', 'Manage users, roles, API keys, encrypted secrets, worker access policies, schedules, and system health from the same interface.'],
    ],
    differentiators: [
      ['Automation is versioned as code', 'A workflow is materialized into readable script files, relations, and real artifact bytes—not trapped in an opaque database document.'],
      ['Control without network inversion', 'Workers poll the control plane, execute locally in the correct OS/runtime context, and report status and logs back incrementally.'],
      ['One definition, many operating contexts', 'Branches and worker assignments become independently schedulable variants while retaining one governed workflow identity.'],
    ],
    architecture: [
      ['Operator experience', 'React Flow renders the workflow graph, Monaco edits node code, Zustand manages editing state, and domain-specific API clients isolate transport concerns.'],
      ['HTTP control plane', 'Express routes validate requests and apply JWT/API-key authentication and RBAC before delegating to framework-free domain services.'],
      ['Execution engine', 'The DAG executor handles roots, conditions, goto transitions, subflows, per-node retry, node/flow timeouts, variables, and local-versus-worker dispatch.'],
      ['Infrastructure adapters', 'Dedicated adapters implement Git object storage and smart HTTP, AES encryption, cron scheduling, filesystem events, artifacts, and execution logs.'],
      ['Worker execution plane', 'Stateless workers register, heartbeat, long-poll for jobs, execute through runtime adapters, and stream logs and completion state back.'],
    ],
    governance: [
      ['Identity and authorization', 'Password or proxy/SSO authentication, JWT sessions, API keys, and admin/operator/viewer RBAC enforced at API routes.'],
      ['Secrets by reference', 'AES-256-GCM encrypted values are resolved from named secret references immediately before execution and are not written into logs.'],
      ['Worker trust boundary', 'Per-worker bearer tokens are hashed at rest; access policies can constrain workers by role or named operator.'],
      ['Safe execution hygiene', 'Artifact paths reject traversal and absolute paths, production refuses placeholder secrets, and stranded jobs are reconciled after restarts or worker loss.'],
    ],
    useCases: [
      'Cross-system industrial and enterprise integration',
      'Scheduled data preparation and file processing',
      'OS-specific automation distributed across workers',
      'Governed browser automation and operational runbooks',
    ],
    workflow: ['Model the workflow', 'Select branch and execution context', 'Schedule or trigger a variant', 'Supervise every node live'],
    deployment:
      'Designed for self-hosted environments. The central server hosts the API and web panel; lightweight workers can run near target systems. MongoDB or embedded NeDB storage is selected by configuration, while packaged Windows executables simplify controlled deployment.',
    status: 'Source code is publicly available on GitHub.',
  },
  {
    slug: 'graph-model-studio',
    repository: 'https://github.com/antoine-marchal/graph-model-studio',
    number: '02',
    name: 'Graph Model Studio',
    eyebrow: 'Architecture workbench',
    tagline: 'A flexible way to model complex situations, architectures, processes, plans, and relationships without losing the underlying structure.',
    description:
      'A local-first workbench I created to explore complexity through multiple views while keeping one editable, source-controlled model underneath.',
    image: '/projects/graph-model-studio/demo/2-c4-containers-components.png',
    alt: 'Graph Model Studio C4 commerce platform view with systems, containers, components, and relationships',
    imagePosition: 'center center',
    tags: ['React Flow', 'Monaco', 'Tauri', 'VS Code'],
    highlights: ['Complex situations made explorable', '22 modeling and analytical notations', 'One model across code and diagrams'],
    facts: [
      ['22', 'Notation families'],
      ['2', 'Layout engines'],
      ['5', 'Export formats'],
      ['0', 'Required servers'],
    ],
    problem:
      'Complex situations rarely fit into a single diagram. Architecture, behavior, planning, risk, dependencies, and stakeholder concerns each require a different view, while ordinary drawing tools duplicate information and quickly drift. I created Graph Model Studio to keep these views connected to one model that can be edited visually or as readable text.',
    audience:
      'Systems, enterprise, solution, and software architects; engineering teams maintaining architecture-as-code; and technical authors embedding living diagrams in documentation.',
    tour: [
      {
        title: 'Explain the system at multiple C4 levels',
        copy: 'Move from context to containers and components while every view stays anchored to the same architecture model.',
        image: '/projects/graph-model-studio/demo/2-c4-containers-components.png',
        alt: 'C4 containers and components view for a commerce platform in Graph Model Studio',
      },
      {
        title: 'Connect architecture to business process',
        copy: 'Use BPMN swimlanes, events, gateways, tasks, and message flows to show how responsibilities cross organizational boundaries.',
        image: '/projects/graph-model-studio/demo/4-bpmn.png',
        alt: 'BPMN order-handling process with sales, fulfillment, and carrier swimlanes',
      },
      {
        title: 'Make interactions unambiguous',
        copy: 'Describe end-to-end behavior with notation-aware sequence views that reveal actors, services, data stores, calls, and returns.',
        image: '/projects/graph-model-studio/demo/3-unlsequence.png',
        alt: 'UML sign-in sequence connecting a user, browser, identity API, and identity database',
      },
      {
        title: 'Turn delivery plans into model views',
        copy: 'Represent phases, dependencies, milestones, and dates in the same workbench used for structural architecture.',
        image: '/projects/graph-model-studio/demo/4-gantt.png',
        alt: 'Release plan Gantt view with discovery, build, quality assurance, and launch phases',
      },
      {
        title: 'Bring risk into the architecture conversation',
        copy: 'Use analytical views alongside system models so technical choices, delivery priorities, and business exposure remain connected.',
        image: '/projects/graph-model-studio/demo/RiskMatrix.png',
        alt: 'Risk matrix view in Graph Model Studio plotting likelihood and impact',
      },
    ],
    more: [
      {
        title: 'Code and canvas together',
        copy: 'Edit readable Graph Model Code and the diagram side by side, with both surfaces synchronized around the same model.',
        image: '/projects/graph-model-studio/mainapp.png',
        alt: 'Graph Model Studio split workspace with explorer, code editor, graph, and inspector',
      },
      {
        title: 'Relationship semantics',
        copy: 'Define labels, direction, multiplicity, notation-specific relation types, colors, and pinned anchors from the inspector.',
        image: '/projects/graph-model-studio/inspectorrelation.png',
        alt: 'Graph Model Studio relationship inspector beside a UML graph',
      },
      {
        title: 'Model explorer',
        copy: 'Navigate elements, relationships, and focused viewpoints without duplicating the underlying architecture.',
        image: '/projects/graph-model-studio/explorerview.png',
        alt: 'Graph Model Studio explorer showing views, elements, and relations',
      },
      {
        title: 'VS Code integration',
        copy: 'Work with the same interactive model inside the editor and render model blocks directly in technical documentation.',
        image: '/projects/graph-model-studio/vscodeintegration.png',
        alt: 'Graph Model Studio interactive graph integrated in Visual Studio Code',
      },
    ],
    capabilities: [
      ['Bidirectional editing', 'DSL changes update the graph; canvas moves, renames, relationships, properties, and layout changes serialize back to Graph Model Code.'],
      ['Twenty-two notation families', 'Use C4, ArchiMate, BPMN, flowcharts, UML, ERD, sequence, tree, mind map, PERT, Gantt, Git, Ishikawa, quadrant, timeline, matrix, Sankey, radar, XY, bar, and generic models.'],
      ['Notation-aware behavior', 'Render correct shapes and markers plus specialized sequence lifelines, Gantt date scales, PERT critical paths, UML compartments, ERD cardinality, and analytic charts.'],
      ['Multiple views, one model', 'Create purpose-specific views with include rules, stored positions, sizes, routing, and view-level layout without duplicating elements.'],
      ['Deep canvas editing', 'Nest containers, resize nodes, pin edge anchors, reconnect relations, edit inline, align selections, snap to grid, and navigate with Explorer and Inspector.'],
      ['Deterministic layout', 'Choose a native cycle-aware layered engine or Dagre; lay out the entire nested view or only selected sibling groups while preserving their visual center.'],
      ['Portable publishing', 'Export JSON, Graph Model Code, Mermaid, PlantUML, and PNG; render GMC fences directly inside VS Code Markdown preview.'],
      ['Native and editor-native', 'Use the Tauri desktop application or the VS Code extension with diagnostics, completion, hover, symbols, go-to-definition, and a fully interactive graph preview.'],
    ],
    differentiators: [
      ['One source of truth', 'Monaco and React Flow do not exchange screenshots—they dispatch against the same typed model and serialized DSL.'],
      ['Semantics before decoration', 'A central notation registry drives menus, properties, colors, shapes, relation markers, and default dimensions consistently.'],
      ['Documentation becomes executable', 'GMC blocks in Markdown can render diagrams in preview and batch-export publication-ready PNGs without creating hidden workspace files.'],
    ],
    architecture: [
      ['Domain core', 'Framework-independent Zod types, model commands, lexer, parser, AST conversion, serializer, notation registry, layouts, and exporters remain independently testable.'],
      ['Shared model store', 'Zustand stores model, DSL source, views, selection, history, and preferences; Immer-backed commands reserialize and persist a local draft.'],
      ['Code surface', 'Monaco debounces DSL edits, reparses the model, reports diagnostics, and preserves valid view positions across reconciliation.'],
      ['Graph surface', 'A model-to-flow bridge resolves visible elements, recursively sizes nested containers, lays out each level, and emits notation-correct React Flow nodes and edges.'],
      ['Delivery surfaces', 'The browser workspace, Tauri desktop shell, and VS Code extension reuse the same model, parser, serializer, notation, layout, and renderer foundations.'],
    ],
    governance: [
      ['Local-first by design', 'Models and drafts stay on the user’s machine; no hosted account or remote modeling service is required.'],
      ['Human-readable native format', 'A `.gmc` file contains model semantics, views, and layout in source-control-friendly text.'],
      ['Explicit persistence', 'Browser drafts auto-save locally; native and VS Code documents follow their host’s normal open, dirty-state, undo, save, and source-control behavior.'],
      ['Typed validation', 'Zod schemas and parser diagnostics identify invalid structures before they become silently corrupted diagrams.'],
    ],
    useCases: [
      'Systems-of-systems and C4 architecture views',
      'Process, planning, risk, and dependency modeling',
      'Architecture-as-code maintained in Git',
      'Living diagrams embedded in technical documentation',
    ],
    workflow: ['Describe the model in GMC', 'Create a focused view', 'Refine it visually', 'Publish without model drift'],
    deployment:
      'Runs locally as a browser workspace, a Tauri desktop application, or an interactive VS Code extension. The architecture model remains portable text, and the modeling core requires no server-side component.',
    status: 'Source code is publicly available on GitHub.',
  },
  {
    slug: 'nodebi',
    repository: 'https://github.com/antoine-marchal/nodebi',
    number: '03',
    name: 'NodeBI',
    eyebrow: 'Decision workspace',
    tagline: 'A simple, effective workspace for staying in control of data and turning it into information people can use.',
    description:
      'A self-hosted BI workspace I created to keep the path from source data to dashboard understandable, controlled, and easy to operate.',
    image: '/projects/nodebi/demo/dashboardedit1.png',
    alt: 'NodeBI decision dashboard editor with metrics, gauges, charts, widget palette, and inspector',
    imagePosition: 'center top',
    tags: ['React', 'Express', 'MongoDB', 'Windows'],
    highlights: ['Keep control of the data path', 'Build useful views without BI overhead', 'Share governed, read-only dashboards'],
    facts: [
      ['13', 'Widget types'],
      ['2', 'Data adapters'],
      ['3', 'Access roles'],
      ['1', 'Portable executable'],
    ],
    problem:
      'Many operational needs call for a clear dashboard, not a large BI programme. I created NodeBI to provide a shorter path: connect a known source, shape and explain the information, compose the view, and share it while retaining control over access and deployment. The aim is deliberately simple—help people master their data and give it meaning.',
    audience:
      'Operational teams, SMEs, industrial organizations, and internal product teams that need controlled dashboards, straightforward deployment, and ownership of their data path.',
    tour: [
      {
        title: 'Compose the complete decision surface',
        copy: 'Combine KPIs, grouped metrics, gauges, charts, tables, and narrative guidance on one resizable operational canvas.',
        image: '/projects/nodebi/demo/dashboardedit1.png',
        alt: 'NodeBI complete capability dashboard with widget palette, metrics, gauges, charts, and inspector',
      },
      {
        title: 'Configure real analysis in context',
        copy: 'Bind widgets to governed sources, filters, aggregations, formats, and custom pipelines while seeing the resulting decision view.',
        image: '/projects/nodebi/demo/dashboardedit2.png',
        alt: 'NodeBI editor showing regional bars, trend lines, incident severity, and metric configuration',
      },
      {
        title: 'Give teams a calm workspace',
        copy: 'Organize dashboards by tags, sources, and namespaces so people can find the decision surface they need without navigating a BI maze.',
        image: '/projects/nodebi/demo/dashboardhome.png',
        alt: 'NodeBI workspace overview with dashboard search, tags, source, and management actions',
      },
    ],
    more: [
      {
        title: 'Governed data sources',
        copy: 'Register NeDB or MongoDB sources while keeping connection details and filesystem access on the server.',
        image: '/projects/nodebi/datasources.png',
        alt: 'NodeBI governed data source administration',
      },
      {
        title: 'Controlled sharing',
        copy: 'Create signed, optionally expiring links that expose a read-only dashboard without exposing source credentials.',
        image: '/projects/nodebi/urlsharing.png',
        alt: 'NodeBI read-only dashboard reached through a signed link',
      },
      {
        title: 'Namespaces and access',
        copy: 'Organize dashboards into explicit scopes and grant access by role or individual account.',
        image: '/projects/nodebi/namespacescontrol.png',
        alt: 'NodeBI namespace access control workspace',
      },
      {
        title: 'Users and roles',
        copy: 'Separate administrative, authoring, and viewing responsibilities with admin, operator, and viewer roles.',
        image: '/projects/nodebi/rbac.png',
        alt: 'NodeBI user and role administration',
      },
    ],
    capabilities: [
      ['Composable dashboard canvas', 'Drag and resize 13 widget types: six chart forms, tables, KPIs, stat cards, gauges, Markdown text, metric groups, and chart groups.'],
      ['Practical data modeling', 'Bind widgets to NeDB or MongoDB, select collections and fields, aggregate by count/sum/average/min/max, or provide MongoDB pipelines.'],
      ['Guided configuration', 'Collection discovery and sample-document inspection power field autocomplete while global date and search filters shape a complete dashboard.'],
      ['Reliable authoring', 'Auto-save editor state, import/export JSON, retain revisions, restore previous versions, and separate designer from read-only viewer experiences.'],
      ['Presentation and export', 'Open dashboards full-screen and export decision views as PDF or image for offline communication.'],
      ['Controlled external sharing', 'Create signed read-only links with optional expiration; shared queries are limited to data sources embedded in that dashboard.'],
      ['Workspace governance', 'Use ownership, namespaces, role-based and user-specific namespace grants, plus admin/operator/viewer permissions.'],
      ['Operational themes', 'Light, dark, and system modes adapt the same workspace for authoring, wallboard, and everyday viewing contexts.'],
    ],
    differentiators: [
      ['Close to the data', 'The application queries embedded files or MongoDB from the self-hosted server rather than uploading operational data to a third-party SaaS.'],
      ['Sharing without credential exposure', 'Public links carry signed scope, receive dashboards with secrets removed, and can query only a source already bound to that dashboard.'],
      ['An upgradeable Windows appliance', 'The web client is embedded in one executable while `.env` configuration and persistent data remain separate for replacement and rollback.'],
    ],
    architecture: [
      ['Experience layer', 'React, Material UI, React Grid Layout, Recharts, Handsontable, and Zustand provide home, designer, viewer, sharing, and administration workspaces.'],
      ['Application API', 'Express and TypeScript routes handle authentication, dashboards, revisions, namespaces, queries, health, and signed sharing.'],
      ['Query boundary', 'NeDB and MongoDB adapters expose discovery, find, and aggregation behind row caps, namespace checks, and path confinement.'],
      ['Metadata and secrets', 'Embedded NeDB stores dashboards, users, revisions, and namespaces; connection material is hydrated server-side and stripped from browser responses.'],
      ['Packaging boundary', 'The production client is embedded in the Node executable while runtime configuration and the complete data directory remain external and backup-friendly.'],
    ],
    governance: [
      ['Role-aware permissions', 'Admins govern the workspace, operators own and edit dashboards, and viewers receive read-only access inside permitted namespaces.'],
      ['Namespace isolation', 'Every dashboard belongs to a namespace whose access can be granted by role or individual account.'],
      ['Protected query paths', 'Viewers cannot submit arbitrary data sources, local paths are constrained to the user-data root, and response rows are capped.'],
      ['Production safeguards', 'Passwords use bcrypt, sessions use JWT, sharing uses an independent signing secret, and startup rejects placeholder production secrets.'],
    ],
    useCases: [
      'Operational performance and production dashboards',
      'Internal management and KPI workspaces',
      'Read-only reporting for controlled audiences',
      'Lightweight BI beside existing MongoDB applications',
    ],
    workflow: ['Connect a governed source', 'Compose the decision view', 'Review and revise', 'Share a controlled experience'],
    deployment:
      'Delivered as a self-hosted Windows application. The browser client is embedded in `nodebi.exe`; `.env` and the persistent data directory stay beside it so an upgrade replaces only the executable. MongoDB is required only for dashboards that use it.',
    status: 'Source code is publicly available on GitHub.',
  },
];

export default projects;
